import axios from 'axios';
import {
  buildBrokerAcceptPayload,
  buildBrokerPromocodePayload,
  buildOrgUserAcceptPayload,
} from './buildOnboardingRegisterPayload';
import { ONBOARDING_USER_TYPE, toOnboardingUserTypeEnum, resolveOnboardingUserId } from './onboardingLinkParams';
import { isEmailAlreadyStaged, mergePendingInviteRows } from './pendingInviteWorkflow';

const API_BASE = process.env.REACT_APP_API_BASE;

/** Query `key` values for GET onboarding/subscription/verify. */
export const SUBSCRIPTION_VERIFY_KEY = {
  SUBSCRIPTION_OR_INVITE: 1,
  PROMO_CODE: 2,
};

/**
 * POST /v{version}/onboarding/broker/accept
 * Broker completes onboarding from an invitation (userId + userType; no token).
 */
export function acceptBrokerOnboarding(registration, options) {
  const url = `${API_BASE}onboarding/broker/accept`;
  const body = buildBrokerAcceptPayload(registration, options);
  return axios.post(url, body);
}

/**
 * POST /v{version}/onboarding/broker/promocode-subscription
 * Legacy endpoint — promo / subscription-key onboarding now uses orguser/accept.
 */
export function acceptBrokerPromocodeOnboarding(registration, options) {
  const url = `${API_BASE}onboarding/broker/promocode-subscription`;
  const body = buildBrokerPromocodePayload(registration, options);
  return axios.post(url, body);
}

/**
 * POST /v{version}/onboarding/broker/resend-invitation
 * Resend broker admin-user invitation email (ResendBrokerInvitationViewModel).
 */
export function resendBrokerOnboardingInvitation({ brokerageId, email }) {
  const url = `${API_BASE}onboarding/broker/resend-invitation`;
  return axios.post(url, {
    brokerageId: Number(brokerageId),
    email: String(email ?? '').trim(),
  });
}

/**
 * POST /v{version}/onboarding/orguser/accept
 * Organisation / end-user onboarding (invitation link, subscription key, or promo code).
 */
export function acceptOrgUserOnboarding(registration, options) {
  const url = `${API_BASE}onboarding/orguser/accept`;
  const body = buildOrgUserAcceptPayload(registration, options);
  return axios.post(url, body);
}

function resolveOrgUserAcceptOptions(registration, linkParams = {}, options = {}) {
  const orgUserId = resolveOnboardingUserId(linkParams, registration) || options.userId;

  return {
    ...options,
    userId: orgUserId,
    invitationId: orgUserId,
  };
}

/**
 * Routes registration by userType + userId / promoCode (token is not used).
 *
 * Broker invitation (userType broker + userId) → POST onboarding/broker/accept
 * Promo / subscription key (and all non-broker paths) → POST onboarding/orguser/accept
 */
export function submitOnboardingRegistration(registration, linkParams = {}, options = {}) {
  const userType = registration.userType || linkParams.userType;
  const requestOptions = { ...options, userType };
  const userId = resolveOnboardingUserId(linkParams, registration);
  const isBrokerInvitation =
    Boolean(userId) && (userType === ONBOARDING_USER_TYPE.BROKER || linkParams.isBrokerPath);

  if (isBrokerInvitation) {
    return acceptBrokerOnboarding(registration, { ...requestOptions, userId });
  }

  return acceptOrgUserOnboarding(registration, resolveOrgUserAcceptOptions(registration, linkParams, requestOptions));
}

/**
 * Normalises POST onboarding/orguser/accept — returns user id for email OTP verification.
 * @param {import('axios').AxiosResponse|{ data?: unknown }} response
 */
export function parseOrgUserAcceptResponse(response) {
  const payload = response?.data ?? response;
  const nested = payload?.data && typeof payload.data === 'object' ? payload.data : payload;
  const userId =
    nested?.userId ?? nested?.id ?? nested?.user?.userId ?? nested?.user?.id ?? payload?.userId ?? payload?.id;

  return {
    userId: userId != null ? String(userId).trim() : '',
    message: nested?.message ?? payload?.message ?? '',
  };
}

/**
 * POST /v{version}/auth/2fa-verify — verify registration email OTP (LoginVerificationModel).
 * Onboarding has no separate email-confirmation step; isEmailVerification tells the API to treat this as email verification.
 */
export function verifyOnboardingRegistrationOtp({ userId, otpCode, rememberMe = false }) {
  const url = `${API_BASE}auth/2fa-verify`;
  return axios.post(url, {
    userId: String(userId ?? '').trim(),
    otpCode: String(otpCode ?? '').trim(),
    rememberMe: Boolean(rememberMe),
    isEmailVerification: true,
  });
}

/**
 * POST /v{version}/auth/credential — resend registration email OTP using account credentials.
 */
export function resendOnboardingRegistrationOtp({ email, password }) {
  const url = `${API_BASE}auth/credential`;
  return axios.post(url, {
    email: String(email ?? '').trim(),
    password: password ?? '',
  });
}

/**
 * GET /v{version}/onboarding/validate-invitation?id=<userId>&userType=<1|2|3>
 * Validates an invitation before showing the registration form.
 */
export function validateInvitation({ id, userType } = {}) {
  const url = `${API_BASE}onboarding/validate-invitation`;
  return axios.get(url, {
    params: {
      id,
      userType: toOnboardingUserTypeEnum(userType) ?? userType,
    },
  });
}

/** @deprecated Prefer validateInvitation — kept for existing call sites. */
export function validateOrgUserInvitation(userId, userType) {
  return validateInvitation({ id: userId, userType });
}

/**
 * GET /v{version}/onboarding/validate-email?email=<email>
 * Checks whether an email is available for onboarding registration.
 */
export function verifyOnboardingAccountEmail(email) {
  const url = `${API_BASE}onboarding/validate-email`;
  return axios.get(url, { params: { email: String(email ?? '').trim() } });
}

/**
 * GET /v{version}/onboarding/subscription/verify?key=&value=
 * Pre-registration only: verifies subscription key or promo code before onboarding opens.
 *
 * key=1 — subscription key
 * key=2 — promo code
 */
export function verifyOnboardingSubscription({ key, value }) {
  const url = `${API_BASE}onboarding/subscription/verify`;
  return axios.get(url, { params: { key, value: trimCode(value) } });
}

/**
 * Verifies subscription key or promo code entered on the activation gate.
 * Promo code takes precedence when both are provided.
 */
export function verifyActivationCredentials({ subscriptionKey, promoCode } = {}) {
  const key = trimCode(subscriptionKey);
  const promo = trimCode(promoCode);

  if (promo) {
    return verifyOnboardingSubscription({ key: SUBSCRIPTION_VERIFY_KEY.PROMO_CODE, value: promo });
  }

  if (key) {
    return verifyOnboardingSubscription({
      key: SUBSCRIPTION_VERIFY_KEY.SUBSCRIPTION_OR_INVITE,
      value: key,
    });
  }

  return Promise.reject(new Error('Enter a subscription key or promo code.'));
}

/**
 * Pre-registration gate only (subscription key or promo code), not invitation links.
 * Promo / key is never treated as broker invitation onboarding.
 */
export function usesSubscriptionActivationVerify(linkParams = {}) {
  if (linkParams.hasInvitationUser || linkParams.userId) return false;
  if (linkParams.promoCode || linkParams.subscriptionKey) return true;
  if (linkParams.isBrokerPath) return false;
  return linkParams.isNormalUserPath || linkParams.isOrgUserPath || !linkParams.userType;
}

function trimCode(value) {
  return value == null ? '' : String(value).trim();
}

/**
 * @param {unknown} payload
 * @returns {string[]}
 */
export function extractUploadIds(payload) {
  if (!payload || typeof payload !== 'object') return [];

  if (Array.isArray(payload.uploadIds)) {
    return payload.uploadIds.filter(Boolean).map(String);
  }

  if (payload.uploadId != null) return [String(payload.uploadId)];
  if (payload.id != null) return [String(payload.id)];

  const nested = payload.data;
  if (nested && typeof nested === 'object') {
    return extractUploadIds(nested);
  }

  return [];
}

/**
 * Extracts user IDs from bulk import responses (legacy fallback).
 * @param {unknown} payload
 * @returns {string[]}
 */
export function extractUserIds(payload) {
  if (!payload || typeof payload !== 'object') return [];

  if (Array.isArray(payload.userIds)) return payload.userIds.filter(Boolean).map(String);
  if (Array.isArray(payload.ids)) return payload.ids.filter(Boolean).map(String);

  if (Array.isArray(payload.createdUsers)) {
    return payload.createdUsers
      .map((user) => user?.userId)
      .filter(Boolean)
      .map(String);
  }

  return extractUploadIds(payload);
}

/** GET /v{version}/onboarding/broker/export-template */
export function downloadOnboardingUsersImportTemplate() {
  const url = `${API_BASE}onboarding/broker/export-template`;
  return axios.get(url, { responseType: 'arraybuffer' });
}

/**
 * POST /v{version}/onboarding/orgusers/import
 * Imports org users from an Excel file; returns uploadId values for send-invitations.
 *
 * @param {FormData} formData — must include `file`; `brokerageId` appended when provided
 * @param {{ brokerageId?: number|string }} [options]
 */
export function importOrgUsers(formData, { brokerageId } = {}) {
  const url = `${API_BASE}onboarding/orgusers/import`;
  if (brokerageId != null && !formData.has('brokerageId')) {
    formData.append('brokerageId', String(brokerageId));
  }
  return axios.post(url, formData);
}

/**
 * Builds POST body for staging a single org user invitee.
 */
export function buildOrgUserInviteeCreateBody(data = {}) {
  const body = {
    email: String(data.email ?? '').trim(),
    firstName: String(data.firstName ?? '').trim(),
    lastName: String(data.lastName ?? '').trim(),
  };

  if (data.roleId != null && String(data.roleId).trim() !== '') {
    body.roleId = String(data.roleId);
  }

  const optionalStringFields = ['fullBusinessName', 'tradingName', 'registrationNumber', 'planName', 'currency'];

  optionalStringFields.forEach((field) => {
    const value = data[field];
    if (value == null) return;
    const trimmed = String(value).trim();
    if (trimmed) body[field] = trimmed;
  });

  return body;
}

/**
 * POST /v{version}/onboarding/orgusers/invitees
 * Stages a single org user (no email sent).
 */
export function createOrgUserInvitee(data) {
  const url = `${API_BASE}onboarding/orgusers/invitees`;
  return axios.post(url, buildOrgUserInviteeCreateBody(data));
}

function getDuplicateUserMessage(payload, email) {
  const duplicates = Array.isArray(payload?.duplicateUsers) ? payload.duplicateUsers : [];
  if (!duplicates.length) return null;

  const duplicate = duplicates[0];
  if (duplicate?.reason) return String(duplicate.reason).trim();
  return `User ${duplicate?.email || email} is already listed.`;
}

/**
 * Normalizes GET /onboarding/orgusers/invitees response to GetBulkUploadusersDto[].
 * @param {unknown} payload
 * @returns {object[]}
 */
export function parseOrgUserInviteesResponse(payload) {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;
  if (typeof payload !== 'object') return [];

  if (Array.isArray(payload.data)) return payload.data;
  if (Array.isArray(payload.invitees)) return payload.invitees;
  if (Array.isArray(payload.items)) return payload.items;

  return [];
}

/** GET /v{version}/onboarding/orgusers/invitees — staged users (source of truth). */
export async function fetchOrgUserInvitees() {
  const url = `${API_BASE}onboarding/orgusers/invitees`;
  const response = await axios.get(url);
  return { ...response, data: parseOrgUserInviteesResponse(response?.data) };
}

/**
 * PUT /v{version}/onboarding/orgusers/invitees/{id}
 * @param {number|string} id
 * @param {object} body — GetBulkUploadusersDto
 */
export function updateOrgUserInvitee(id, body) {
  const url = `${API_BASE}onboarding/orgusers/invitees/${id}`;
  return axios.put(url, body);
}

/** DELETE /v{version}/onboarding/orgusers/invitees/{id} */
export function deleteOrgUserInvitee(id) {
  const url = `${API_BASE}onboarding/orgusers/invitees/${id}`;
  return axios.delete(url);
}

/**
 * Builds PUT body from existing invitee plus form updates (GetBulkUploadusersDto).
 */
export function buildOrgUserInviteeUpdateBody(raw = {}, updates = {}, inviteeId) {
  const source = raw && typeof raw === 'object' ? raw : {};
  const resolvedId = inviteeId ?? source.id ?? updates.id;
  return {
    id: resolvedId != null ? Number(resolvedId) : undefined,
    email: String(updates.email ?? source.email ?? '').trim(),
    firstName: updates.firstName ?? source.firstName ?? null,
    lastName: updates.lastName ?? source.lastName ?? null,
    roleName: updates.roleName ?? source.roleName ?? null,
    fullBusinessname: updates.fullBusinessname ?? source.fullBusinessname ?? source.fullBusinessName ?? null,
    tradingName: updates.tradingName ?? source.tradingName ?? null,
    businessRegistrationNumber: updates.businessRegistrationNumber ?? source.businessRegistrationNumber ?? null,
    isBroker: updates.isBroker ?? source.isBroker ?? false,
    planName: updates.planName ?? source.planName ?? null,
    currency: updates.currency ?? source.currency ?? null,
  };
}

/** GET /v{version}/brokerages/{brokerageId}/subscriptions */
export function fetchBrokerageSubscriptionsDashboard(brokerageId) {
  const url = `${API_BASE}brokerages/${brokerageId}/subscriptions`;
  return axios.get(url);
}

/**
 * Phase 1: stage one user (no email). Phase 2: refetch subscriptions + invitees.
 * Treats HTTP 2xx with empty body as success.
 */
export async function stageManualOrgUserInvitee(data) {
  const { brokerageId } = data;
  const body = buildOrgUserInviteeCreateBody(data);
  const trimmedEmail = body.email;

  const [subscriptionsRes, inviteesRes] = await Promise.all([
    fetchBrokerageSubscriptionsDashboard(brokerageId),
    fetchOrgUserInvitees(),
  ]);

  const subscriptions = subscriptionsRes?.data?.subscriptions || [];
  const invitees = inviteesRes?.data || [];
  const pendingRows = mergePendingInviteRows(subscriptions, invitees);

  if (isEmailAlreadyStaged(trimmedEmail, pendingRows)) {
    throw Object.assign(new Error('This email is already on the pending list.'), { isUserFacing: true });
  }

  const postRes = await createOrgUserInvitee(body);
  const payload = postRes?.data;
  const duplicateMessage = getDuplicateUserMessage(payload, trimmedEmail);
  if (duplicateMessage) {
    throw Object.assign(new Error(duplicateMessage), { isUserFacing: true });
  }

  const [nextSubscriptionsRes, nextInviteesRes] = await Promise.all([
    fetchBrokerageSubscriptionsDashboard(brokerageId),
    fetchOrgUserInvitees(),
  ]);

  return {
    response: postRes,
    subscriptionsData: nextSubscriptionsRes?.data || {},
    invitees: nextInviteesRes?.data || [],
  };
}

function mapInviteeRow(user, { roleName } = {}) {
  const email = user?.email || '';
  if (!email) return null;

  return {
    key: `invitee-${user.userId || email}`,
    userId: user.userId != null ? String(user.userId) : null,
    assignedTo: email,
    email,
    assignName: user.firstName || user.name || null,
    organisationName: user.organisationName || user.fullBusinessName || user.businessName || null,
    planName: user.planName || null,
    roleName: user.roleName || roleName || null,
    brokerName: user.roleName || roleName || null,
    inviteStatus: 'pending',
    isPendingInvitee: true,
  };
}

/**
 * Maps POST onboarding/orgusers/invitees response for the Assign tab.
 * @param {unknown} payload
 * @param {{ email?: string, roleId?: string|number, roles?: object[] }} [context]
 * @returns {object|null}
 */
export function parseCreatedOrgUserInvitee(payload, { email, roleId, roles = [] } = {}) {
  if (!payload || typeof payload !== 'object') return null;

  const nested = payload.data && typeof payload.data === 'object' ? payload.data : payload;

  if (Array.isArray(nested.createdUsers) && nested.createdUsers.length > 0) {
    const [createdUser] = parseImportedUsers(nested);
    if (createdUser) return createdUser;
  }

  const result = nested.result && typeof nested.result === 'object' ? nested.result : nested;
  const role = roles.find((item) => String(item.id || item.roleId) === String(roleId || result.roleId));
  const roleName = role?.name || role?.roleName || result.roleName || null;
  const resolvedEmail = result.email || email;
  const userId = result.userId ?? result.id ?? nested.userId ?? nested.id;

  return mapInviteeRow(
    {
      ...result,
      email: resolvedEmail,
      userId,
      roleName,
    },
    { roleName },
  );
}

/**
 * Fallback assign-tab row when the invitee API succeeds without a detailed body.
 */
export function buildFallbackInviteeRow({ email, roleId, roles = [], uploadId } = {}) {
  const role = roles.find((item) => String(item.id || item.roleId) === String(roleId));
  const roleName = role?.name || role?.roleName || null;

  const row = mapInviteeRow(
    {
      email,
      roleName,
    },
    { roleName },
  );

  return row ? { ...row, uploadId: uploadId || row.uploadId || null } : null;
}

/**
 * Maps POST onboarding/orgusers/import response rows for the Assign tab.
 * @param {unknown} payload
 * @returns {object[]}
 */
export function parseImportedUsers(payload) {
  if (!payload || typeof payload !== 'object') return [];

  const uploadId = payload.uploadId != null ? String(payload.uploadId) : null;
  const createdUsers = Array.isArray(payload.createdUsers) ? payload.createdUsers : [];

  return createdUsers
    .map((user) => {
      const row = mapInviteeRow(user, { roleName: user.roleName });
      return row ? { ...row, uploadId, isImportedUser: true } : null;
    })
    .filter(Boolean);
}

/**
 * POST /v{version}/onboarding/orgusers/send-invitations
 * Sends or re-sends invitations for staged org user invitees.
 *
 * @param {{ uploadIds?: string[] }} params
 */
export function sendOnboardingInvitations({ uploadIds } = {}) {
  const uploads = (Array.isArray(uploadIds) ? uploadIds : []).filter(Boolean).map(String);

  if (!uploads.length) {
    return Promise.reject(new Error('No users selected to invite.'));
  }

  const url = `${API_BASE}onboarding/orgusers/send-invitations`;
  return axios.post(url, { uploadIds: uploads });
}

/**
 * POST /v{version}/onboarding/orgusers/invitation-status
 * Same body as send-invitations. Used to follow a queued send job.
 *
 * @param {{ uploadIds?: string[] }} params
 */
export function fetchInvitationStatus({ uploadIds } = {}) {
  const uploads = (Array.isArray(uploadIds) ? uploadIds : []).filter(Boolean).map(String);

  if (!uploads.length) {
    return Promise.reject(new Error('No invitations to check.'));
  }

  const url = `${API_BASE}onboarding/orgusers/invitation-status`;
  return axios.post(url, { uploadIds: uploads });
}

function pickNumericField(source, ...keys) {
  if (!source || typeof source !== 'object') return null;
  for (let i = 0; i < keys.length; i += 1) {
    const value = source[keys[i]];
    if (value != null && value !== '') {
      const parsed = Number(value);
      if (!Number.isNaN(parsed)) return parsed;
    }
  }
  return null;
}

function hasSendInvitationCounts(source) {
  if (!source || typeof source !== 'object') return false;
  return (
    pickNumericField(source, 'sentCount', 'sentcount', 'successCount') != null ||
    pickNumericField(source, 'failedCount', 'failedcount', 'failed') != null ||
    pickNumericField(source, 'total', 'totalRows', 'totalCount') != null
  );
}

function normalizeSendInvitationsPayload(payload) {
  if (!payload || typeof payload !== 'object') return null;

  const candidates = [payload];
  if (payload.data && typeof payload.data === 'object') candidates.push(payload.data);
  if (payload.result && typeof payload.result === 'object') candidates.push(payload.result);

  const match = candidates.find(hasSendInvitationCounts);
  if (match) return match;

  return payload.data && typeof payload.data === 'object' ? payload.data : payload;
}

function formatSendInvitationErrorHint(errors) {
  if (!Array.isArray(errors) || !errors.length) return '';

  const first = errors[0];
  if (typeof first === 'string' && first.trim()) return ` ${first.trim()}`;

  if (first && typeof first === 'object') {
    const message = first.message || first.reason || first.error || first.detail;
    if (message) return ` ${String(message).trim()}`;
  }

  return '';
}

const QUEUED_JOB_STATUSES = new Set(['queued', 'pending', 'processing', 'sending', 'inprogress']);
const SENT_JOB_STATUSES = new Set(['sent', 'completed', 'complete', 'success', 'succeeded', 'invitationsent', 'done']);
const FAILED_JOB_STATUSES = new Set(['failed', 'error', 'failure']);

const INVITATION_STATUS_POLL_ATTEMPTS = 4;
const INVITATION_STATUS_POLL_MS = 1500;

function normalizeInvitationJobStatus(status) {
  return String(status ?? '')
    .trim()
    .toLowerCase()
    .replace(/[\s_-]/g, '');
}

function invitationStatusRows(data) {
  if (Array.isArray(data)) return data;
  if (!data || typeof data !== 'object') return [];

  const list = data.items || data.results || data.uploads || data.invitations || data.statuses;
  return Array.isArray(list) ? list : [];
}

function summarizeInvitationStatusRows(rows) {
  return rows.reduce(
    (summary, row) => {
      const status = normalizeInvitationJobStatus(
        row?.status ?? row?.invitationStatus ?? row?.inviteStatus,
      );
      if (SENT_JOB_STATUSES.has(status)) return { ...summary, sentCount: summary.sentCount + 1 };
      if (FAILED_JOB_STATUSES.has(status)) return { ...summary, failedCount: summary.failedCount + 1 };
      return { ...summary, queuedCount: summary.queuedCount + 1 };
    },
    { sentCount: 0, failedCount: 0, queuedCount: 0 },
  );
}

function emptySendInvitationsResult() {
  return {
    total: 0,
    sentCount: 0,
    failedCount: 0,
    errors: [],
    jobId: null,
    status: null,
    message: '',
  };
}

/**
 * Normalizes POST onboarding/orgusers/send-invitations and invitation-status.
 * Queued send: { jobId, total, status: "Queued", message }.
 */
export function parseSendInvitationsResult(payload) {
  const data = normalizeSendInvitationsPayload(payload);
  if (!data || typeof data !== 'object') return emptySendInvitationsResult();

  const rows = invitationStatusRows(data);
  const rowSummary = rows.length ? summarizeInvitationStatusRows(rows) : null;
  const sentCount = pickNumericField(data, 'sentCount', 'sentcount', 'successCount') ?? rowSummary?.sentCount ?? 0;
  const failedCount = pickNumericField(data, 'failedCount', 'failedcount', 'failed') ?? rowSummary?.failedCount ?? 0;
  const total = pickNumericField(data, 'total', 'totalRows', 'totalCount') ?? (rows.length || sentCount + failedCount || 0);
  const errors = Array.isArray(data.errors) ? data.errors : [];
  const jobId = data.jobId != null ? String(data.jobId) : null;
  const message = typeof data.message === 'string' ? data.message.trim() : '';
  let status = data.status != null ? String(data.status) : null;

  if (!status && rowSummary) {
    if (rowSummary.queuedCount > 0) status = 'Queued';
    else if (rowSummary.failedCount > 0 && rowSummary.sentCount === 0) status = 'Failed';
    else if (rowSummary.sentCount > 0) status = 'Completed';
  }

  return { total, sentCount, failedCount, errors, jobId, status, message };
}

export function isInvitationJobInProgress(result) {
  const status = normalizeInvitationJobStatus(result?.status);
  if (QUEUED_JOB_STATUSES.has(status)) return true;
  if (SENT_JOB_STATUSES.has(status) || FAILED_JOB_STATUSES.has(status)) return false;
  if (status) return false;
  return result?.total > 0 && !result?.sentCount && !result?.failedCount;
}

function waitForInvitationStatus(ms) {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

/**
 * Follows a queued send with POST invitation-status until it leaves Queued, or the poll budget ends.
 * A status-check failure keeps the last known result.
 */
export async function pollInvitationStatus({ uploadIds, initial } = {}) {
  async function attempt(remaining, latest) {
    if (remaining <= 0 || !isInvitationJobInProgress(latest)) return latest;

    await waitForInvitationStatus(INVITATION_STATUS_POLL_MS);

    try {
      const response = await fetchInvitationStatus({ uploadIds });
      const next = parseSendInvitationsResult(response);
      const hasUpdate = next.status || next.message || next.sentCount || next.failedCount || next.total;
      const merged = hasUpdate
        ? { ...latest, ...next, jobId: next.jobId || latest.jobId || null }
        : latest;
      return attempt(remaining - 1, merged);
    } catch {
      return latest;
    }
  }

  return attempt(INVITATION_STATUS_POLL_ATTEMPTS, initial || emptySendInvitationsResult());
}

export function formatSendInvitationsFeedback(result, { requestedCount = 0, isResend = false } = {}) {
  const { total, sentCount, failedCount, errors, message } = result;
  const errorHint = formatSendInvitationErrorHint(errors);
  const hasApiCounts = sentCount > 0 || failedCount > 0 || total > 0;
  const effectiveTotal = total || sentCount + failedCount || requestedCount;
  const apiMessage = typeof message === 'string' ? message.trim() : '';

  if (apiMessage && isInvitationJobInProgress(result) && sentCount === 0 && failedCount === 0) {
    return { type: 'success', text: apiMessage };
  }

  if (!hasApiCounts) {
    if (requestedCount === 1) {
      return {
        type: 'success',
        text: isResend ? 'Invite re-sent successfully.' : 'Invite sent successfully.',
      };
    }

    if (requestedCount > 1) {
      return {
        type: 'success',
        text: `${requestedCount} invite${requestedCount === 1 ? '' : 's'} processed successfully.`,
      };
    }

    return { type: 'success', text: 'Invitations processed successfully.' };
  }

  if (sentCount > 0 && failedCount === 0) {
    if (requestedCount === 1 || effectiveTotal === 1) {
      return {
        type: 'success',
        text: isResend ? 'Invite re-sent successfully.' : 'Invite sent successfully.',
      };
    }

    if (effectiveTotal > sentCount) {
      return {
        type: 'success',
        text: `${sentCount} of ${effectiveTotal} invitation${effectiveTotal === 1 ? '' : 's'} sent successfully.`,
      };
    }

    return {
      type: 'success',
      text: `${sentCount} invitation${sentCount === 1 ? '' : 's'} sent successfully.`,
    };
  }

  if (sentCount > 0 && failedCount > 0) {
    return {
      type: 'warning',
      text: `${sentCount} of ${effectiveTotal} invitation${effectiveTotal === 1 ? '' : 's'} sent. ${failedCount} could not be sent.${errorHint}`,
    };
  }

  if (failedCount === 0) {
    const queuedCount = effectiveTotal || requestedCount;
    if (queuedCount === 1) {
      return {
        type: 'success',
        text: isResend
          ? 'Re-send submitted. Invitations are sent in a batch, so the updated status will appear shortly.'
          : 'Invitation submitted. Invitations are sent in a batch, so the updated status will appear shortly.',
      };
    }

    return {
      type: 'success',
      text: `${queuedCount} invitation${queuedCount === 1 ? '' : 's'} submitted. They are sent in a batch, so updated statuses will appear shortly.`,
    };
  }

  return {
    type: 'error',
    text: `No invitations were sent.${failedCount ? ` ${failedCount} failed.` : ''}${errorHint}`,
  };
}

function normalizeOrgUsersImportPayload(payload) {
  if (!payload || typeof payload !== 'object') return null;
  if (payload.data && typeof payload.data === 'object') return payload.data;
  return payload;
}

/**
 * Normalizes POST onboarding/orgusers/import response
 * (e.g. { createdUsers, excludedUsers, failedCount }).
 */
export function parseOrgUsersImportResult(payload) {
  const data = normalizeOrgUsersImportPayload(payload);
  if (!data) {
    return { total: 0, successCount: 0, failedCount: 0, failures: [] };
  }

  const createdUsers = Array.isArray(data.createdUsers) ? data.createdUsers : [];
  const excludedUsers = Array.isArray(data.excludedUsers) ? data.excludedUsers : [];
  const successCount = createdUsers.length;
  const failedCount = pickNumericField(data, 'failedCount', 'failedcount', 'failed') ?? excludedUsers.length;
  const total = pickNumericField(data, 'total', 'totalRows', 'totalCount') ?? successCount + failedCount;

  const failures = excludedUsers
    .map((item) => ({
      rowNumber: pickNumericField(item, 'rowNumber', 'row', 'RowNumber', 'Row'),
      email: String(item?.email ?? '').trim(),
      reason: String(item?.reason ?? item?.message ?? item?.error ?? 'Unknown error').trim(),
    }))
    .filter((item) => item.rowNumber != null || item.email || item.reason);

  return { total, successCount, failedCount, failures };
}

export function formatOrgUsersImportFeedback(result) {
  const { total, successCount, failedCount } = result;

  if (successCount > 0 && failedCount === 0) {
    return {
      type: 'success',
      text: `${successCount} user${successCount === 1 ? '' : 's'} imported. Send invites from Subscription Onboarding when ready.`,
    };
  }

  if (successCount > 0 && failedCount > 0) {
    return {
      type: 'warning',
      text: `${successCount} of ${total} record${total === 1 ? '' : 's'} imported. ${failedCount} failed — see summary for details.`,
    };
  }

  return {
    type: 'error',
    text: `Import completed but no records were uploaded.${failedCount ? ` ${failedCount} failed.` : ''}`,
  };
}
